import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";

// ─── Response Types ───────────────────────────────────────────────────────────

export interface DashboardMetrics {
    total_tests: number;
    total_passed: number;
    total_failed: number;
    total_blocked: number;
    pass_rate: number;
    automation_rate: number;
    execution_velocity: number;
    active_projects: number;
    generated_at: string;
}

export interface ExecutionTrendPoint {
    date: string;
    total: number;
    passed: number;
    failed: number;
    blocked: number;
}

export interface ExecutionTrendResponse {
    data: ExecutionTrendPoint[];
    period_days: number;
    generated_at: string;
}

export interface QualityTrendPoint {
    date: string;
    pass_rate: number;
    defect_count: number;
    automation_rate: number;
}

export interface QualityTrendResponse {
    data: QualityTrendPoint[];
    period_days: number;
    generated_at: string;
}

export interface ProjectHealth {
    project_id: number;
    project_name: string;
    health_score: number;
    total_tests: number;
    pass_rate: number;
    automation_rate: number;
    recent_failures: number;
    risk_level: "low" | "medium" | "high";
}

export interface ProjectHealthResponse {
    projects: ProjectHealth[];
    generated_at: string;
}

export interface ExecutionSummaryItem {
    execution_id: number;
    test_case_id: number;
    test_case_name: string;
    testplan_id: number;
    testplan_name: string;
    build_id: number;
    build_name: string;
    status: "PASSED" | "FAILED" | "BLOCKED" | "NOT RUN";
    executor: string | null;
    executed_at: string;
}

export interface ExecutionSummaryResponse {
    total_executions: number;
    passed: number;
    failed: number;
    blocked: number;
    pass_rate: number;
    executions: ExecutionSummaryItem[];
    generated_at: string;
}

export interface CoverageReport {
    total_requirements: number;
    covered_requirements: number;
    coverage_percentage: number;
    uncovered_requirements: Array<{ id: number; title: string }>;
    generated_at: string;
}

export interface ExecutiveReportMetrics {
    total_executions: number;
    passed: number;
    failed: number;
    blocked: number;
    pass_rate: number;
    total_projects: number;
    total_testcases: number;
    automation_rate: number;
}

export interface ExecutiveReport {
    title: string;
    generated_at: string;
    metrics: ExecutiveReportMetrics;
}

export interface ManagementReportMetrics {
    total_testers: number;
    avg_execution_time_minutes: number;
    pass_rate: number;
    active_testplans: number;
    open_defects: number;
}

export interface ManagementReport {
    title: string;
    generated_at: string;
    metrics: ManagementReportMetrics;
}

export interface MyMetrics {
    total_executed: number;
    total_passed: number;
    total_failed: number;
    pass_rate: number;
    automation_created: number;
    last_execution_date: string | null;
    generated_at: string;
}

// ─── Filters ─────────────────────────────────────────────────────────────────

export interface ExecutionSummaryFilters {
    project_id?: number;
    testplan_id?: number;
    build_id?: number;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useDashboardMetrics(projectId?: number) {
    return useQuery<DashboardMetrics>({
        queryKey: ["advanced-reports", "dashboard", projectId],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (projectId != null) params.set("project_id", String(projectId));
            const res = await apiClient.get(`/api/advanced-reports/dashboard${params.size ? `?${params}` : ""}`);
            return res.data;
        },
        staleTime: 60_000,
    });
}

export function useExecutionTrend(projectId?: number, days = 30) {
    return useQuery<ExecutionTrendResponse>({
        queryKey: ["advanced-reports", "trends/execution", projectId, days],
        queryFn: async () => {
            const params = new URLSearchParams({ days: String(days) });
            if (projectId != null) params.set("project_id", String(projectId));
            const res = await apiClient.get(`/api/advanced-reports/trends/execution?${params}`);
            return res.data;
        },
        staleTime: 60_000,
    });
}

export function useQualityTrend(days = 30) {
    return useQuery<QualityTrendResponse>({
        queryKey: ["advanced-reports", "quality-trend", days],
        queryFn: async () => {
            const res = await apiClient.get(`/api/advanced-reports/quality-trend?days=${days}`);
            return res.data;
        },
        staleTime: 60_000,
    });
}

export function useProjectHealth() {
    return useQuery<ProjectHealthResponse>({
        queryKey: ["advanced-reports", "project-health"],
        queryFn: async () => {
            const res = await apiClient.get("/api/advanced-reports/project-health");
            return res.data;
        },
        staleTime: 60_000,
    });
}

export function useExecutionSummary(filters: ExecutionSummaryFilters = {}) {
    return useQuery<ExecutionSummaryResponse>({
        queryKey: ["advanced-reports", "execution-summary", filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters.project_id != null)  params.set("project_id",  String(filters.project_id));
            if (filters.testplan_id != null) params.set("testplan_id", String(filters.testplan_id));
            if (filters.build_id != null)    params.set("build_id",    String(filters.build_id));
            if (filters.start_date)          params.set("start_date",  filters.start_date);
            if (filters.end_date)            params.set("end_date",     filters.end_date);
            if (filters.limit != null)       params.set("limit",        String(filters.limit));
            if (filters.offset != null)      params.set("offset",       String(filters.offset));
            const res = await apiClient.get(`/api/advanced-reports/execution-summary?${params}`);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useCoverageReport(projectId?: number) {
    return useQuery<CoverageReport>({
        queryKey: ["advanced-reports", "coverage", projectId],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (projectId != null) params.set("project_id", String(projectId));
            const res = await apiClient.get(`/api/advanced-reports/coverage${params.size ? `?${params}` : ""}`);
            return res.data;
        },
        staleTime: 120_000,
    });
}

export function useExecutiveReport(projectId?: number) {
    return useQuery<ExecutiveReport>({
        queryKey: ["advanced-reports", "executive", projectId],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (projectId != null) params.set("project_id", String(projectId));
            const res = await apiClient.get(`/api/advanced-reports/executive${params.size ? `?${params}` : ""}`);
            return res.data;
        },
        staleTime: 120_000,
    });
}

export function useManagementReport(projectId?: number) {
    return useQuery<ManagementReport>({
        queryKey: ["advanced-reports", "management", projectId],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (projectId != null) params.set("project_id", String(projectId));
            const res = await apiClient.get(`/api/advanced-reports/management${params.size ? `?${params}` : ""}`);
            return res.data;
        },
        staleTime: 120_000,
    });
}

export function useMyMetrics() {
    return useQuery<MyMetrics>({
        queryKey: ["advanced-reports", "my-metrics"],
        queryFn: async () => {
            const res = await apiClient.get("/api/advanced-reports/my-metrics");
            return res.data;
        },
        staleTime: 60_000,
    });
}